import React, { useState, useEffect } from 'react';
import { CreditCard, Building2, Zap, CheckCircle, Gift } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { refreshNotifications } from './NotificationBell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PAYMENT_METHODS = [
  { id: 'yoco', name: 'Yoco', desc: 'Real card payments (test mode)', icon: CreditCard, color: 'bg-blue-100 text-blue-600', badge: 'LIVE' },
  { id: 'payfast', name: 'PayFast', desc: 'Cards & EFT (demo)', icon: Building2, color: 'bg-purple-100 text-purple-600' },
  { id: 'ozow', name: 'Ozow', desc: 'Instant EFT (demo)', icon: Zap, color: 'bg-emerald-100 text-emerald-600' },
];

export default function PaymentModal({ open, onOpenChange, errand, onSuccess }) {
  const [selected, setSelected] = useState('yoco');
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(false);
  const [creditBalance, setCreditBalance] = useState(0);

  useEffect(() => {
    if (open) {
      fetch(`${BACKEND_URL}/api/referrals/my`, { credentials: 'include' })
        .then(r => r.ok ? r.json() : null)
        .then(d => d && setCreditBalance(d.credit_balance || 0))
        .catch(() => {});
    }
  }, [open]);

  const handlePay = async () => {
    setProcessing(true);
    try {
      // Real Yoco flow: create hosted checkout and redirect
      if (selected === 'yoco') {
        const res = await fetch(`${BACKEND_URL}/api/payments/yoco/create-session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ errand_id: errand.errand_id })
        });
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.detail || 'Yoco session failed');
        }
        const data = await res.json();
        if (data.fully_covered_by_credit) {
          setSuccess(true);
          setTimeout(() => {
            toast.success('Payment covered by credit!');
            onSuccess?.();
            onOpenChange(false);
            setSuccess(false);
            refreshNotifications();
          }, 1200);
          return;
        }
        // Redirect to Yoco hosted checkout
        window.location.assign(data.redirect_url);
        return;
      }

      // Mock flow for PayFast / Ozow
      const res = await fetch(`${BACKEND_URL}/api/payments/process`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ errand_id: errand.errand_id, payment_method: selected })
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Payment failed');
      }

      setSuccess(true);
      setTimeout(() => {
        toast.success('Payment successful!');
        onSuccess?.();
        onOpenChange(false);
        setSuccess(false);
        refreshNotifications();
      }, 1500);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setProcessing(false);
    }
  };

  if (!errand) return null;

  const creditApplied = Math.min(creditBalance, errand.price);
  const amountDue = Math.max(0, errand.price - creditApplied);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="payment-modal" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-slate-900">
            {success ? 'Payment Complete' : 'Complete Payment'}
          </DialogTitle>
        </DialogHeader>

        {success ? (
          <div className="py-8 text-center">
            <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-12 h-12 text-yellow-600" strokeWidth={1.5} />
            </div>
            <p className="text-lg font-medium text-slate-900">R{errand.price.toFixed(2)} paid</p>
            {creditApplied > 0 && (
              <p className="text-sm text-yellow-600 mt-1">R{creditApplied.toFixed(2)} credit applied</p>
            )}
            <p className="text-slate-600 mt-2">Your errand is now available for runners</p>
          </div>
        ) : (
          <div className="space-y-5 mt-4">
            <div className="bg-slate-50 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="text-sm text-slate-600">Errand total</p>
                  <p className="text-2xl font-bold text-slate-900">R{errand.price.toFixed(2)}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-600">{errand.errand_type === 'parcel' ? 'Parcel Delivery' : 'Queue Assistance'}</p>
                  <p className="text-sm text-slate-500">{errand.stops} stops</p>
                </div>
              </div>
              {creditApplied > 0 && (
                <div data-testid="credit-applied-row" className="mt-3 pt-3 border-t border-slate-200 space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-1.5 text-yellow-600">
                      <Gift className="w-4 h-4" />
                      <span>Credit applied</span>
                    </div>
                    <span className="font-medium text-yellow-600">−R{creditApplied.toFixed(2)}</span>
                  </div>
                  <div className="flex items-center justify-between pt-1">
                    <span className="font-bold text-slate-900">You pay</span>
                    <span data-testid="amount-due" className="text-xl font-bold text-slate-900">R{amountDue.toFixed(2)}</span>
                  </div>
                </div>
              )}
            </div>

            {amountDue > 0 ? (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-3">Select payment method</label>
                <div className="space-y-2">
                  {PAYMENT_METHODS.map((m) => (
                    <button
                      key={m.id}
                      data-testid={`payment-method-${m.id}`}
                      onClick={() => setSelected(m.id)}
                      className={`w-full p-4 rounded-xl border-2 transition-all flex items-center gap-4 ${
                        selected === m.id
                          ? 'border-slate-900 bg-slate-50'
                          : 'border-slate-200 bg-white hover:border-slate-300'
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${m.color}`}>
                        <m.icon className="w-5 h-5" strokeWidth={1.5} />
                      </div>
                      <div className="flex-1 text-left">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-900">{m.name}</p>
                          {m.badge && (
                            <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-bold">
                              {m.badge}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-500">{m.desc}</p>
                      </div>
                      {selected === m.id && (
                        <CheckCircle className="w-5 h-5 text-slate-900" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-center">
                <Gift className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
                <p className="font-medium text-slate-900">Fully covered by credit!</p>
                <p className="text-sm text-slate-600 mt-1">No payment method needed.</p>
              </div>
            )}

            <Button
              data-testid="pay-now-btn"
              onClick={handlePay}
              disabled={processing}
              className="w-full bg-yellow-400 hover:bg-yellow-500 text-slate-900 rounded-full py-3 font-bold shadow-md btn-hover"
            >
              {processing ? 'Processing...' : amountDue > 0 ? (selected === 'yoco' ? `Continue to Yoco →` : `Pay R${amountDue.toFixed(2)}`) : 'Confirm & Publish'}
            </Button>

            {amountDue > 0 && (
              <p className="text-xs text-slate-500 text-center">
                Secure payment powered by South African payment gateways
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
